import { AlertTriangle, ArrowDown, ArrowUp, Minus, ChevronsUp } from 'lucide-react'
import type { Task, TaskPriority, TaskStatus } from '@/api/tasks'
import type { TKey } from '@/i18n'
import type { Tone } from '@/components/ui/misc'
import { Badge } from '@/components/ui/misc'
import { useT } from '@/i18n'
import { cn } from '@/lib/utils'

export const STATUS_LABEL: Record<TaskStatus, TKey> = {
  todo: 'tasks.status.todo',
  in_progress: 'tasks.status.in_progress',
  review: 'tasks.status.review',
  done: 'tasks.status.done',
  cancelled: 'tasks.status.cancelled',
}

export const STATUS_TONE: Record<TaskStatus, Tone> = {
  todo: 'neutral',
  in_progress: 'info',
  review: 'warning',
  done: 'success',
  cancelled: 'outline',
}

export const PRIORITY_LABEL: Record<TaskPriority, TKey> = {
  low: 'tasks.priority.low',
  medium: 'tasks.priority.medium',
  high: 'tasks.priority.high',
  critical: 'tasks.priority.critical',
}

export function StatusBadge({ status }: { status: TaskStatus }) {
  const t = useT()
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{t(STATUS_LABEL[status] ?? 'tasks.status.todo')}</Badge>
}

export function PriorityIcon({ priority, className, withLabel }: { priority: TaskPriority; className?: string; withLabel?: boolean }) {
  const t = useT()
  const Icon = priority === 'critical' ? ChevronsUp : priority === 'high' ? ArrowUp : priority === 'low' ? ArrowDown : Minus
  const color = priority === 'critical' ? 'text-danger' : priority === 'high' ? 'text-warning-ink' : priority === 'low' ? 'text-info' : 'text-muted'
  return (
    <span className={cn('inline-flex items-center gap-1', color, className)} title={t(PRIORITY_LABEL[priority])}>
      <Icon className="size-3.5" aria-hidden />
      {withLabel ? <span className="text-[12.5px]">{t(PRIORITY_LABEL[priority])}</span> : <span className="sr-only">{t(PRIORITY_LABEL[priority])}</span>}
    </span>
  )
}

export function OverdueMark() {
  const t = useT()
  return (
    <span className="inline-flex items-center gap-1 text-danger">
      <AlertTriangle className="size-3.5" aria-hidden />
      <span className="text-[12px] font-medium">{t('tasks.overdue')}</span>
    </span>
  )
}

export function isOpen(t: Task) {
  return t.status !== 'done' && t.status !== 'cancelled'
}

export function isOverdue(t: Task, now = Date.now()) {
  return t.overdue || (!!t.due_at && isOpen(t) && new Date(t.due_at).getTime() < now)
}
